/**
 * FacebookPostBanner — compact Facebook post reference bar with modal dialog
 * for full post preview (caption, image, permalink, and post details).
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { ExternalLink, Eye, Image as ImageIcon, Heart, MessageCircle, Share2 } from 'lucide-react'
import { cleanIdentifier, isNumericOrRawId } from '../../utils/formatCustomerName.js'
import { api } from '../../api/client.js'

interface PostEngagement {
  reactions: number
  comments: number
  shares: number
}

export interface FacebookPostBannerProps {
  /** Facebook post ID in the `{pageId}_{postId}` format. */
  postId?: string | null
  /** Post caption. Empty for posts without text. */
  postMessage?: string | null
  /** Original post image URL captured during comment ingestion. */
  postPicture?: string | null
  /** Direct link to the Facebook post. */
  permalink?: string | null
  /** Compact variant for message threads. */
  compact?: boolean
  /** Facebook account holder's name (e.g. Page name, profile name, or user display name). */
  accountHolderName?: string | null
  /** Facebook ID (e.g. page ID or sender ID or post ID). */
  accountId?: string | null
  /** Custom button label (defaults to 'View') */
  buttonLabel?: string
}

function imageCandidates(postId: string, picture: string): string[] {
  const candidates: string[] = []

  if (postId) {
    candidates.push(
      `/api/agent/facebook-posts/${encodeURIComponent(postId)}/image`,
    )
  }

  if (picture && !candidates.includes(picture)) {
    candidates.push(picture)
  }

  return candidates
}

export function FacebookPostBanner({
  postId,
  postMessage,
  postPicture,
  permalink,
  compact = false,
  accountHolderName,
  accountId,
  buttonLabel = 'View',
}: FacebookPostBannerProps): JSX.Element | null {
  const [dialogVisible, setDialogVisible] = useState(false)
  const [engagement, setEngagement] = useState<PostEngagement | null>(null)
  const [loadingEngagement, setLoadingEngagement] = useState(false)

  const message = postMessage?.trim() ?? ''
  const picture = postPicture?.trim() ?? ''
  const link = permalink?.trim() ?? ''
  const id = postId?.trim() ?? ''
  const accId = accountId?.trim() ?? ''

  // Format account holder name cleanly
  const rawAccountName = accountHolderName?.trim() ?? ''
  const displayAccountName = useMemo(() => {
    const cleaned = cleanIdentifier(rawAccountName)
    if (cleaned && !isNumericOrRawId(cleaned)) {
      return cleaned
    }
    return 'Facebook Post'
  }, [rawAccountName])

  // Display ID for reference
  const displayId = id || accId || ''

  const candidates = useMemo(
    () => imageCandidates(id, picture),
    [id, picture],
  )

  const [candidateIndex, setCandidateIndex] = useState(0)

  useEffect(() => {
    setCandidateIndex(0)
  }, [candidates])

  // Fetch engagement data when dialog opens
  useEffect(() => {
    if (dialogVisible && id && !engagement && !loadingEngagement) {
      setLoadingEngagement(true)
      api
        .get(`/agent/facebook-posts/${encodeURIComponent(id)}/engagement`)
        .then((data) => setEngagement(data as PostEngagement))
        .catch(() => {
          // Silently fail - engagement is optional
          setEngagement({ reactions: 0, comments: 0, shares: 0 })
        })
        .finally(() => setLoadingEngagement(false))
    }
  }, [dialogVisible, id, engagement, loadingEngagement])

  // If there's neither a post ID, caption, picture, nor link, nothing to render.
  // An account ID alone without any post context must not trigger rendering.
  if (!message && !picture && !link && !id) {
    return null
  }

  const imageSrc =
    candidateIndex >= 0 && candidateIndex < candidates.length
      ? candidates[candidateIndex]
      : undefined

  const imageFailed = candidates.length > 0 && !imageSrc

  const handleImageError = () => {
    setCandidateIndex((current) =>
      current + 1 < candidates.length ? current + 1 : -1,
    )
  }

  return (
    <>
      {/* Compact Facebook Post Reference Bar */}
      <section
        data-testid="facebook-post-banner"
        className={[
          'flex min-w-0 items-center justify-between gap-3 border border-[#1877F2]/20 bg-[#1877F2]/5 transition-colors',
          compact
            ? 'mb-2.5 rounded-[3px] px-2.5 py-1.5'
            : 'shrink-0 rounded-[3px] px-3.5 py-2',
        ].join(' ')}
        aria-label="Facebook post reference"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[3px] bg-[#1877F2] text-white shadow-xs"
            aria-hidden="true"
          >
            <FacebookIcon />
          </span>

          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs font-semibold text-slate-800">
              {displayAccountName}
            </span>

            {displayId && (
              <>
                <span className="text-slate-300" aria-hidden="true">·</span>
                <span
                  className="truncate font-mono text-[11px] text-slate-500"
                  title={displayId}
                >
                  ID: {displayId}
                </span>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setDialogVisible(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-[3px] bg-[#1877F2] px-2.5 py-1 text-xs font-medium text-white shadow-xs transition-colors hover:bg-[#166fe5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1877F2]"
          aria-label="View Post"
        >
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{buttonLabel}</span>
        </button>
      </section>

      {/* Full Facebook Post Modal Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[3px] bg-[#1877F2] text-white"
              aria-hidden="true"
            >
              <FacebookIcon />
            </span>
            <div className="min-w-0">
              <span className="text-sm font-bold text-slate-900">
                Facebook Post
              </span>
              {displayAccountName !== 'Facebook Post' && (
                <span className="ml-1.5 text-xs font-normal text-slate-500">
                  by {displayAccountName}
                </span>
              )}
            </div>
          </div>
        }
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '560px', maxWidth: '95vw' }}
        modal
        dismissableMask
        className="p-fluid"
      >
        <div className="max-h-[75vh] space-y-4 overflow-y-auto pr-1">
          {/* Post metadata header */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div className="min-w-0 space-y-0.5">
              <p className="font-semibold text-slate-800">
                Account: <span className="font-medium text-slate-700">{displayAccountName}</span>
              </p>
              {displayId && (
                <p className="truncate font-mono text-[11px] text-slate-500" title={displayId}>
                  Post ID: {displayId}
                </p>
              )}
            </div>

            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1 rounded-[3px] bg-white border border-slate-200 px-2.5 py-1 text-xs font-medium text-[#1877F2] transition-colors hover:bg-blue-50 hover:text-blue-800"
              >
                <span>Open on Facebook</span>
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </div>

          {/* Post Engagement Metrics */}
          {engagement && (
            <div className="flex items-center gap-4 rounded-[3px] border border-[#1877F2]/20 bg-[#1877F2]/5 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-xs text-slate-700">
                <Heart className="h-4 w-4 text-[#1877F2]" fill="currentColor" />
                <span className="font-semibold">{engagement.reactions.toLocaleString()}</span>
                <span className="text-slate-500">reactions</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-700">
                <MessageCircle className="h-4 w-4 text-[#1877F2]" />
                <span className="font-semibold">{engagement.comments.toLocaleString()}</span>
                <span className="text-slate-500">comments</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-700">
                <Share2 className="h-4 w-4 text-[#1877F2]" />
                <span className="font-semibold">{engagement.shares.toLocaleString()}</span>
                <span className="text-slate-500">shares</span>
              </div>
            </div>
          )}
          
          {loadingEngagement && (
            <div className="flex items-center justify-center gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              <span className="animate-pulse">Loading engagement...</span>
            </div>
          )}

          {/* Post image */}
          {imageSrc && (
            <div className="flex justify-center overflow-hidden rounded-[3px] border border-slate-200 bg-slate-900/5 p-2">
              <img
                key={imageSrc}
                src={imageSrc}
                alt="Facebook post attachment"
                loading="lazy"
                className="block max-h-[380px] w-auto max-w-full rounded-[3px] object-contain shadow-xs"
                onError={handleImageError}
              />
            </div>
          )}

          {/* Image fallback if candidates failed */}
          {imageFailed && (
            <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
              <ImageIcon className="h-4 w-4 shrink-0 text-slate-400" />
              <span>Post image could not be loaded or is currently unavailable.</span>
            </div>
          )}

          {/* Post caption / text */}
          {message ? (
            <div className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Caption
              </span>
              <div className="max-h-64 overflow-y-auto rounded-[3px] border border-slate-200 bg-slate-50 p-3.5 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap break-words">
                {message}
              </div>
            </div>
          ) : !picture ? (
            <div className="rounded-[3px] border border-slate-100 bg-slate-50 p-3 text-xs italic text-slate-500">
              This post has no caption.
            </div>
          ) : (
            <div className="rounded-[3px] border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Photo post without text caption.
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-[3px] border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <span>View Original Post</span>
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
            </a>
          )}
          <button
            type="button"
            onClick={() => setDialogVisible(false)}
            className="rounded-[3px] bg-slate-100 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline-none"
          >
            Close
          </button>
        </div>
      </Dialog>
    </>
  )
}

function FacebookIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3 w-3"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.093 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.025 1.792-4.71 4.533-4.71 1.312 0 2.686.236 2.686.236v2.98h-1.514c-1.49 0-1.955.93-1.955 1.887v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.093 24 18.1 24 12.073Z" />
    </svg>
  )
}

export default FacebookPostBanner
